import { DeliveryMethod, OrderFields } from "../../constants";
import useOrderData from "../../context/useOrderData";
import type { TOrderFormData } from "../../models/orderForm";
import type { TPrefillData } from "../../models/prefillData";
import useOrderStorage from "../storage/useOrderStorage";
import useClearConflictingData from "./useClearConflictingData";

type TClearConflictingReceiverDataReturn = {
  handleDestinationCountryChange: (countryCode: string) => void;
  handleDeliveryMethodChange: (deliveryMethod: DeliveryMethod) => void;
};

const getReceiverPrefillValues = (prefillData?: TPrefillData, storedData?: TOrderFormData) => {
  if (prefillData) {
    return {
      addressCountryCode: prefillData.address?.countryCode || undefined,
      lockerCountryCode: prefillData.locker?.countryCode || undefined,
      addressId: prefillData.id || undefined,
      name: prefillData.recipient?.name || undefined,
      phoneNumber: prefillData.recipient?.phone || undefined,
      email: prefillData.recipient?.email || undefined,
      street: prefillData.address?.street || undefined,
      houseNo: prefillData.address?.streetNumber || undefined,
      flatNo: prefillData.address?.flatNo || undefined,
      city: prefillData.address?.city || undefined,
      zipCode: prefillData.address?.zip || undefined,
      locker: prefillData.locker?.lockerId || undefined,
      lockerName: prefillData.locker?.locker || undefined,
    };
  }
  if (storedData) {
    return {
      addressCountryCode: storedData[OrderFields.DESTINATION_COUNTRY] || undefined,
      lockerCountryCode: storedData[OrderFields.DESTINATION_COUNTRY] || undefined,
      addressId: storedData[OrderFields.RECEIVER_ADDRESS_ID] || undefined,
      name: storedData[OrderFields.RECEIVER_NAME] || undefined,
      phoneNumber: storedData[OrderFields.RECEIVER_PHONE_NUMBER] || undefined,
      email: storedData[OrderFields.RECEIVER_EMAIL] || undefined,
      street: storedData[OrderFields.RECEIVER_STREET] || undefined,
      houseNo: storedData[OrderFields.RECEIVER_HOUSE_NO] || undefined,
      flatNo: storedData[OrderFields.RECEIVER_FLAT_NO] || undefined,
      city: storedData[OrderFields.RECEIVER_CITY] || undefined,
      zipCode: storedData[OrderFields.RECEIVER_ZIP_CODE] || undefined,
      locker: storedData[OrderFields.RECEIVER_LOCKER] || undefined,
      lockerName: storedData[OrderFields.RECEIVER_LOCKER_NAME] || undefined,
    };
  }
  return undefined;
};

const useClearConflictingReceiverData = (): TClearConflictingReceiverDataReturn => {
  const { prefillData } = useOrderData();

  const { getData } = useOrderStorage();

  const storedData = getData();

  const prefill = getReceiverPrefillValues(prefillData, storedData);

  const prefillAddressCountryCode = prefill?.addressCountryCode;
  const prefillLockerCountryCode = prefill?.lockerCountryCode;

  const { handleCountryChange: handleDestinationCountryChange, handleDeliveryMethodChange } = useClearConflictingData({
    prefillAddressCountryCode,
    prefillLockerCountryCode,
    fieldNames: {
      addressId: OrderFields.RECEIVER_ADDRESS_ID,
      country: OrderFields.DESTINATION_COUNTRY,
      deliveryMethod: OrderFields.DELIVERY_METHOD,
      name: OrderFields.RECEIVER_NAME,
      phoneNumber: OrderFields.RECEIVER_PHONE_NUMBER,
      email: OrderFields.RECEIVER_EMAIL,
      street: OrderFields.RECEIVER_STREET,
      houseNo: OrderFields.RECEIVER_HOUSE_NO,
      flatNo: OrderFields.RECEIVER_FLAT_NO,
      city: OrderFields.RECEIVER_CITY,
      zipCode: OrderFields.RECEIVER_ZIP_CODE,
      locker: OrderFields.RECEIVER_LOCKER,
      lockerName: OrderFields.RECEIVER_LOCKER_NAME,
      pickupDate: OrderFields.PICKUP_DATE,
      pickupTimeFrom: OrderFields.PICKUP_TIME_FROM,
      pickupTimeTill: OrderFields.PICKUP_TIME_TILL,
    },
    prefill: {
      addressId: prefill?.addressId || undefined,
      name: prefill?.name || undefined,
      phoneNumber: prefill?.phoneNumber || undefined,
      email: prefill?.email || undefined,
      street: prefill?.street || undefined,
      houseNo: prefill?.houseNo || undefined,
      flatNo: prefill?.flatNo || undefined,
      city: prefill?.city || undefined,
      zipCode: prefill?.zipCode || undefined,
      locker: prefill?.locker || undefined,
      lockerName: prefill?.lockerName || undefined,
    },
    addressDeliveryMethods: [DeliveryMethod.DoorToDoor, DeliveryMethod.PudoToDoor],
    lockerDeliveryMethods: [DeliveryMethod.PudoToPudo, DeliveryMethod.DoorToPudo],
    fromLockerDeliveryMethods: [DeliveryMethod.PudoToPudo, DeliveryMethod.PudoToDoor],
  });

  return { handleDestinationCountryChange, handleDeliveryMethodChange };
};
export default useClearConflictingReceiverData;
